import { useWorkspaceStore } from '@easy-expense/data-firestore-client';
import { Icon, IconName } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Separator, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { NavLink, useLocation } from 'react-router-dom';

import { useAppRoutes } from './Routes';
import { WorkspaceSwitcher } from './WorkspaceSwitcher.component';
import { auth } from '../firebase/app';
import { logout } from '../firebase/auth';

function parseRoute(route: string): { pathname: string; search: string } {
  const [pathname = route, query] = route.split('?');
  return { pathname, search: query ? `?${query}` : '' };
}

export const Sidebar: React.FC = () => {
  const [user] = useAuthState(auth);
  const Routes = useAppRoutes();
  const location = useLocation();
  const purgeWorkspace = useWorkspaceStore((w) => w.purge);

  const activeRoute = React.useMemo(() => {
    let best: { route: string; score: number } | null = null;
    for (const item of Routes) {
      const { pathname, search } = parseRoute(item.route);
      if (pathname !== location.pathname) {
        continue;
      }
      const matchesSearch = search === '' || search === location.search;
      if (!matchesSearch) {
        continue;
      }
      const score = (search ? 1000 : 0) + search.length;
      if (!best || score > best.score) {
        best = { route: item.route, score };
      }
    }
    return best?.route ?? null;
  }, [Routes, location.pathname, location.search]);

  return (
    <Layout.Row style={{ width: 240, height: '100vh', flexShrink: 0 }} bg="inputBackground">
      <Layout.Column grow>
        <WorkspaceSwitcher />

        <Separator.Horizontal />

        <Spacer.Vertical size={36} />

        <Layout.Column px={16} style={{ width: '100%' }}>
          {Routes.map((item) => {
            const isActive = activeRoute === item.route;
            return (
              <NavLink to={item.route} key={item.route} style={{ textDecoration: 'none' }}>
                {() => {
                  const [hover, setHover] = React.useState(false);
                  let iconColor: string = theme.colors.gray;
                  if (isActive) {
                    iconColor = theme.colors.brandPrimary;
                  } else if (hover) {
                    iconColor = theme.colors.primary;
                  }

                  return (
                    <Layout.Column>
                      <Layout.Row
                        style={{ backgroundColor: isActive || hover ? '#f4f5f7' : 'transparent' }}
                        radius={8}
                        py={10}
                        px
                        align
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                      >
                        <Icon
                          name={(item.icon + '-outline') as IconName}
                          size={20}
                          color={iconColor}
                        />

                        <Spacer.Horizontal size={16} />

                        <OpenSans.Primary
                          weight={isActive ? 'bold-700' : 'regular-400'}
                          color={isActive || hover ? 'primary' : 'gray'}
                          size="s-16"
                        >
                          {item.name}
                        </OpenSans.Primary>
                      </Layout.Row>
                      <Spacer.Vertical size={4} />
                    </Layout.Column>
                  );
                }}
              </NavLink>
            );
          })}
        </Layout.Column>

        <Spacer.Flex />
        {/* <Layout.Column px py>
        <Mono.Primary size={10}>{JSON.stringify({ Constants }, null, 2)}</Mono.Primary>
      </Layout.Column> */}

        <Layout.Column px py>
          <Spacer.Vertical />
          <Layout.PressableRow
            bg="primary"
            px
            py
            radius
            onClick={() => {
              purgeWorkspace();
              logout();
            }}
            center
            hoverColor={theme.colors.gray}
            border={[0, 'solid', 'gray']}
          >
            <OpenSans.Inverse weight="light-300">{user ? 'Log Out' : 'Log In'}</OpenSans.Inverse>
          </Layout.PressableRow>
        </Layout.Column>
      </Layout.Column>

      <Separator.Vertical />
    </Layout.Row>
  );
};
